import DateUtil from '../util/DateUtil'

export type ProgressType = 'map' | 'dots'
export type ChallengeTheme = 'light' | 'dark'

export interface ChallengeArgument {
  id: string
  name: string
  startDate: Date
  endDate: Date
  goal: number
  restDaysRequired: number
  teamSizeMin: number
  teamSizeMax: number
  teamSizeTarget: number
  progressType: ProgressType
  theme?: ChallengeTheme
  primaryColor?: string
  secondaryColor?: string
  logoUrl?: string
  markUrl?: string
  description?: string
  isTest?: boolean
  shirtImageUrl?: string
  finishLineVideoUrl?: string
  passPrice: number
  teamPassPrice: number
  teamLeaderPassPrice: number
  passPriceId: string
  teamPassPriceId: string
  teamLeaderPassPriceId: string
  allowsFreeAgent?: boolean
  createdAt?: Date
}

export default class Challenge {
  readonly id: string
  readonly name: string
  readonly startDate: Date
  readonly endDate: Date
  readonly goal: number
  readonly restDaysRequired: number
  readonly teamSizeMin: number
  readonly teamSizeMax: number
  readonly teamSizeTarget: number
  readonly progressType: ProgressType
  readonly theme?: ChallengeTheme
  readonly primaryColor?: string
  readonly secondaryColor?: string
  readonly logoUrl?: string
  readonly markUrl?: string
  readonly description?: string
  readonly isTest: boolean
  readonly shirtImageUrl?: string
  readonly finishLineVideoUrl?: string
  readonly passPrice: number
  readonly teamPassPrice: number
  readonly teamLeaderPassPrice: number
  readonly passPriceId: string
  readonly teamPassPriceId: string
  readonly teamLeaderPassPriceId: string
  readonly allowsFreeAgent: boolean
  readonly createdAt?: Date

  constructor(arg: ChallengeArgument) {
    this.id = arg.id
    this.name = arg.name
    this.startDate = arg.startDate
    this.endDate = arg.endDate
    this.goal = arg.goal
    this.restDaysRequired = arg.restDaysRequired
    this.teamSizeMin = arg.teamSizeMin
    this.teamSizeMax = arg.teamSizeMax
    this.teamSizeTarget = arg.teamSizeTarget
    this.progressType = arg.progressType
    this.theme = arg.theme
    this.primaryColor = arg.primaryColor
    this.secondaryColor = arg.secondaryColor
    this.logoUrl = arg.logoUrl
    this.markUrl = arg.markUrl
    this.description = arg.description
    this.isTest = arg.isTest === true
    this.shirtImageUrl = arg.shirtImageUrl
    this.finishLineVideoUrl = arg.finishLineVideoUrl
    this.passPrice = arg.passPrice
    this.teamPassPrice = arg.teamPassPrice
    this.teamLeaderPassPrice = arg.teamLeaderPassPrice
    this.passPriceId = arg.passPriceId
    this.teamPassPriceId = arg.teamPassPriceId
    this.teamLeaderPassPriceId = arg.teamLeaderPassPriceId
    this.allowsFreeAgent = arg.allowsFreeAgent === true
    this.createdAt = arg.createdAt
  }

  static fromPG(row: any): Challenge {
    return new Challenge({
      id: row.id,
      name: row.name,
      startDate: DateUtil.setTo1201AM(row.start_date),
      endDate: DateUtil.setTo1201AM(row.end_date),
      goal: row.goal,
      restDaysRequired: row.rest_days_required,
      teamSizeMin: row.team_size_min,
      teamSizeMax: row.team_size_max,
      teamSizeTarget: row.team_size_target ?? row.default_target_member_count ?? row.team_size_max ?? 10,
      progressType: row.progress_type ?? 'map',
      theme: row.theme === 'dark' ? 'dark' : 'light',
      primaryColor: row.primary_color ?? undefined,
      secondaryColor: row.secondary_color ?? undefined,
      logoUrl: row.logo_url ?? undefined,
      markUrl: row.mark_url ?? undefined,
      description: row.description ?? undefined,
      isTest: row.is_test === true,
      shirtImageUrl: row.shirt_image_url || undefined,
      finishLineVideoUrl: row.finish_line_video_url ?? undefined,
      passPrice: Number(row.pass_price),
      teamPassPrice: Number(row.team_pass_price),
      teamLeaderPassPrice: Number(row.team_leader_pass_price),
      passPriceId: row.pass_price_id,
      teamPassPriceId: row.team_pass_price_id,
      teamLeaderPassPriceId: row.team_leader_pass_price_id,
      allowsFreeAgent: row.allows_free_agent === true,
      createdAt: row.created_at ? new Date(row.created_at) : undefined,
    })
  }

  getDaysRemaining() {
    return DateUtil.easternDaysUntil(DateUtil.getNowInEST(), this.endDate) + 1
  }

  getTotalDays() {
    return DateUtil.easternDaysUntil(this.startDate, this.endDate) + 1
  }

  getPaceProgress() {
    if (process.env.REACT_APP_OVERRIDE_PACE_PROGRESS === 'true') {
      return this.goal * 0.25
    }

    const totalDays = this.getTotalDays()
    if (totalDays <= 0 || !isFinite(totalDays)) return 0

    const daysPassed = Math.min(
      totalDays,
      Math.max(0, totalDays - this.getDaysRemaining() + 1),
    )
    const paceProgress = (daysPassed / totalDays) * this.goal

    return Math.max(0, isFinite(paceProgress) ? paceProgress : 0)
  }

  hasStarted() {
    return DateUtil.easternDaysUntil(this.startDate, DateUtil.getNowInEST()) >= 0
  }

  hasEnded() {
    return DateUtil.easternDaysUntil(this.endDate, DateUtil.getNowInEST()) > 0
  }
}
